import { format } from "date-fns";
import Message from "types/Message";
import React from "react";
import { Download } from "lucide-react";
import { MessageAvatar } from "./MessageAvatar";

interface MessageBalloonProps {
  message: Message;
}

export function MessageBalloon({ message }: MessageBalloonProps) {
  const isRightAligned = [
    "user",
    "teacher",
    "info",
    "question",
    "alert",
  ].includes(message.type);
  const isInternal = ["teacher", "teacher-response"].includes(message.type);

  return (
    <div
      className={`flex ${
        isRightAligned ? "justify-end" : "justify-start"
      } w-full`}
    >
      {!isRightAligned && (
        <div className="flex-shrink-0 mr-1 lg:mr-2 mt-1">
          <MessageAvatar messageType={message.type} size="sm" />
        </div>
      )}
      <div
        className={`chat-message p-2 lg:p-3 rounded-lg shadow-sm text-xs lg:text-sm whitespace-pre-wrap max-w-[85%] lg:max-w-[85%] w-fit`}
        style={{
          backgroundColor: getBackgroundColor(message.type),
          color: getTextColor(message.type),
          minWidth: "100px",
          fontStyle: isInternal ? "italic" : "normal",
        }}
      >
        <p className="break-words overflow-wrap-anywhere">{message.content}</p>

        {message.fileUrl &&
          (isImageUrl(message.fileUrl) ? (
            <div className="mt-2 w-full">
              <img
                src={message.fileUrl}
                alt={getFileName(message.fileUrl)}
                className="rounded-md w-full h-auto max-w-full object-contain"
                style={{ maxHeight: "300px" }}
              />
            </div>
          ) : (
            <div className="mt-2 flex items-center gap-1 lg:gap-2 bg-gray-100 rounded p-1 lg:p-2 max-w-full">
              <span className="font-semibold bg-gray-200 rounded px-1 lg:px-2 py-0.5 text-xs flex-shrink-0">
                {getFileExtension(message.fileUrl)}
              </span>
              <span className="text-xs truncate flex-1">
                {getFileName(message.fileUrl)}
              </span>
              <a
                href={message.fileUrl}
                download={getFileName(message.fileUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-blue-600 hover:underline flex-shrink-0"
                title="Download"
              >
                <Download className="h-3 w-3 lg:h-4 lg:w-4" />
              </a>
            </div>
          ))}

        <p className="text-xs text-gray-500 mt-1 text-right">
          {format(new Date(message.sentAt), "dd/MM/yyyy HH:mm")}
        </p>
      </div>
      {isRightAligned && (
        <div className="flex-shrink-0 ml-1 lg:ml-2 mt-1">
          <MessageAvatar messageType={message.type} size="sm" />
        </div>
      )}
    </div>
  );
}

function getBackgroundColor(type: Message["type"]): string {
  switch (type) {
    case "user":
      return "#DFFFD6"; // light green
    case "assistant":
      return "#FFFFFF"; // white
    case "teacher-response":
      return "#F0F0F0"; // light gray
    case "teacher":
      return "#D2D2D2"; // darker gray
    case "question":
      return "#FFF9C4"; // soft yellow
    case "alert":
      return "#FFCDD2"; // soft red
    case "info":
      return "#96ff7c"; // green
    default:
      return "#FFFFFF";
  }
}

function getTextColor(type: Message["type"]): string {
  return ["teacher", "teacher-response"].includes(type) ? "#444" : "#000";
}

// Helper to check if a URL is an image
function isImageUrl(url: string): boolean {
  return /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(url.split("?")[0]);
}

// Helper to extract file name from URL
function getFileName(url: string): string {
  try {
    return decodeURIComponent(
      url.split("/").pop()?.split("?")[0] || "Archivo adjunto"
    );
  } catch {
    return "Archivo adjunto";
  }
}

// Helper to extract file extension from URL
function getFileExtension(url: string): string {
  const name = getFileName(url);
  const match = name.match(/\.([a-zA-Z0-9]+)$/);
  return match ? match[1].toUpperCase() : "FILE";
}
